import { type ModuleDefinition } from './module-kit';
import { flowModuleKey } from './flows';
import type { FlowFactRule } from './fact-rules';
import { parseFlowPhaseRows, type FlowPhaseRow } from './stage-gates';
import { m } from '@/i18n';
import { errOf, isRecord, ok, type Result } from './types';

/**
 * First-class ontology definitions (docs/FLOW.md, docs/VIEWS.md §2a): the pure
 * core. A definition is a **field** (an `AuthoredField` payload, historical -
 * see below) or a **flow** (a `FlowPayload` - stages + flow-scoped fields),
 * authored once on a context anchor (a group or an item root). This file owns
 * the field-payload shape and its validation, key minting, the flow
 * ModuleDefinition bridge, and flow-payload validation. Pure: no DB, no
 * framework - the effectful hosts (server / read) are the edges.
 *
 * The context-field system (the `ctx:<ownerRef>` value namespace, the alignment
 * ladder, private annotations) was RETIRED 2026-07-16 (docs/VIEWS.md §2a): custom
 * fields now live on FLOWS. The `AuthoredField` payload shape survives because a
 * flow's fields ARE authored fields; the `field`-kind definition row is historical.
 */

// --- Authored field payloads (the ontology carriers) ------------------------

/**
 * **Not the same as `FieldType` in module-kit.ts** (deliberate divergence): this
 * covers only what a person can author in the UI today. The module kit's `FieldType`
 * covers all stored types including internal/derived ones. User words map to
 * technical words across the boundary: `selection` -> `enum`, `actor` -> `ref`
 * (constrained to person/group), `date` -> `timevalue` (grain-carrying; an exact
 * day is the finest grain, docs/FIELDS_AND_TAGS.md §2). Do not try to unify them.
 */
export const AUTHORED_FIELD_TYPES = ['text', 'number', 'date', 'selection', 'actor'] as const;
export type AuthoredFieldType = (typeof AUTHORED_FIELD_TYPES)[number];

/** Text renders as a one-line datum (`line`, the default) or a markdown
 *  document section (`prose`): same stored string, different query affordances
 *  (prose never lanes or sorts). Stored only when `prose`. */
export const AUTHORED_TEXT_VARIANTS = ['line', 'prose'] as const;
export type AuthoredTextVariant = (typeof AUTHORED_TEXT_VARIANTS)[number];

/** Selection and actor fields hold `one` value (default) or `many`
 *  (multi-select / a set of people and groups). Stored only when `many`. */
export const AUTHORED_CARDINALITIES = ['one', 'many'] as const;
export type AuthoredCardinality = (typeof AUTHORED_CARDINALITIES)[number];

export type AuthoredFieldValue = {
  value: string;
  /** optional per-value meaning (surfaced on hover, like the field's). */
  description?: string;
};

/** The `payload` jsonb of a `field` definition (visibility is the row column, not
 *  part of the payload). Name, **Description** (required: orgs misalign on
 *  ontology, so every field must say what it MEANS; the same text is the MCP
 *  tool description an agent reasons over), Type, and Values for selections. */
export type AuthoredField = {
  key: string; // stable id, minted once from the label, never regenerated
  label: string; // the Name
  description: string; // the ontology artifact; required, non-empty
  type: AuthoredFieldType;
  values?: AuthoredFieldValue[]; // selection only
  variant?: AuthoredTextVariant; // text only; present only as 'prose'
  cardinality?: AuthoredCardinality; // selection/actor only; present only as 'many'
  /** Show on cards (the flow-designated highlight, DESIGN_SYSTEM "Board card
   *  anatomy"): member cards render this field's value as a quiet label:value
   *  pair wherever the item appears as a card. Deliberately type-agnostic (a
   *  flag, not a type facet) so new field types inherit it unchanged. Absent =
   *  false; cards cap what they show at {@link HIGHLIGHT_CARD_CAP}. */
  highlight?: boolean;
};

/** How many highlight fields a card renders, definition order, values first.
 *  The card is a summary; past this the item page's Fields section is the
 *  surface. */
export const HIGHLIGHT_CARD_CAP = 3;

/** Mint a stable key from a label: kebab slug, deduped against taken keys.
 *  Minted ONCE at add; renames change the label, never the key. */
export function mintFieldKey(label: string, taken: ReadonlySet<string>): string {
  const base =
    label
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'field';
  let key = base;
  for (let n = 2; taken.has(key); n += 1) key = `${base}-${n}`;
  return key;
}

/** Validate one authored field (strict: the authoring path). */
export function parseAuthoredField(value: unknown): Result<AuthoredField> {
  if (!isRecord(value)) return errOf('invalid_input', m('flowErrors.field.notObject'));
  const key = typeof value.key === 'string' ? value.key.trim() : '';
  if (key.length === 0) return errOf('invalid_input', m('flowErrors.field.needsKey'));
  const label = typeof value.label === 'string' ? value.label.trim() : '';
  if (label.length === 0)
    return errOf('invalid_input', m('flowErrors.field.needsName', { field: key }));
  const description = typeof value.description === 'string' ? value.description.trim() : '';
  if (description.length === 0) {
    return errOf('invalid_input', m('flowErrors.field.needsDescription', { field: label }));
  }
  const type = value.type;
  if (!(AUTHORED_FIELD_TYPES as readonly unknown[]).includes(type)) {
    return errOf('invalid_input', m('flowErrors.field.unknownType', { field: label }));
  }

  let values: AuthoredFieldValue[] | undefined;
  if (type === 'selection') {
    if (!Array.isArray(value.values) || value.values.length === 0) {
      return errOf('invalid_input', m('flowErrors.field.selectionNeedsValue', { field: label }));
    }
    values = [];
    const seen = new Set<string>();
    for (const raw of value.values) {
      // Accept both {value, description?} rows and bare strings.
      const v: Record<string, unknown> = isRecord(raw) ? raw : { value: raw };
      const val = typeof v.value === 'string' ? v.value.trim() : '';
      if (val.length === 0)
        return errOf('invalid_input', m('flowErrors.field.emptyValue', { field: label }));
      if (seen.has(val))
        return errOf(
          'invalid_input',
          m('flowErrors.field.duplicateValue', { field: label, value: val }),
        );
      seen.add(val);
      const desc = typeof v.description === 'string' ? v.description.trim() : '';
      values.push({ value: val, ...(desc ? { description: desc } : {}) });
    }
  } else if (value.values !== undefined) {
    return errOf('invalid_input', m('flowErrors.field.valuesOnSelection', { field: label }));
  }

  let variant: AuthoredTextVariant | undefined;
  if (value.variant !== undefined) {
    if (type !== 'text') {
      return errOf('invalid_input', m('flowErrors.field.variantOnText', { field: label }));
    }
    if (!(AUTHORED_TEXT_VARIANTS as readonly unknown[]).includes(value.variant)) {
      return errOf('invalid_input', m('flowErrors.field.unknownVariant', { field: label }));
    }
    if (value.variant === 'prose') variant = 'prose'; // 'line' is the default; not stored
  }

  let cardinality: AuthoredCardinality | undefined;
  if (value.cardinality !== undefined) {
    if (type !== 'selection' && type !== 'actor') {
      return errOf('invalid_input', m('flowErrors.field.cardinalityOnSelection', { field: label }));
    }
    if (!(AUTHORED_CARDINALITIES as readonly unknown[]).includes(value.cardinality)) {
      return errOf('invalid_input', m('flowErrors.field.unknownCardinality', { field: label }));
    }
    if (value.cardinality === 'many') cardinality = 'many'; // 'one' is the default; not stored
  }

  return ok({
    key,
    label,
    description,
    type: type as AuthoredFieldType,
    ...(values ? { values } : {}),
    ...(variant ? { variant } : {}),
    ...(cardinality ? { cardinality } : {}),
    // Lenient on junk (stored rows must normalize): only literal true counts.
    ...(value.highlight === true ? { highlight: true } : {}),
  });
}

/** Validate a whole authored list: each field, plus unique keys. */
export function parseAuthoredFields(value: unknown): Result<AuthoredField[]> {
  if (!Array.isArray(value)) return errOf('invalid_input', m('flowErrors.fields.notList'));
  const out: AuthoredField[] = [];
  const seen = new Set<string>();
  for (const raw of value) {
    const field = parseAuthoredField(raw);
    if (!field.ok) return field;
    if (seen.has(field.value.key)) {
      return errOf(
        'invalid_input',
        m('flowErrors.fields.duplicateKey', { field: field.value.key }),
      );
    }
    seen.add(field.value.key);
    out.push(field.value);
  }
  return ok(out);
}

/** Lenient normalization for STORAGE reads (a payload from a stored row must
 *  never throw): invalid rows are dropped, not errors. */
export function normalizeAuthoredFields(value: unknown): AuthoredField[] {
  if (!Array.isArray(value)) return [];
  const out: AuthoredField[] = [];
  const seen = new Set<string>();
  for (const raw of value) {
    const field = parseAuthoredField(raw);
    if (field.ok && !seen.has(field.value.key)) {
      seen.add(field.value.key);
      out.push(field.value);
    }
  }
  return out;
}

/**
 * One stored field value as card-display text (the highlight row's value; pure
 * over unknown storage). Null = nothing to show (the card row skips the field
 * entirely - a highlight field with no value is silence, never a ghost).
 * Total over junk: stored module data is normalized, never thrown on. Dates
 * render as stored (ISO date) - compact and unambiguous on a card; richer
 * locale formatting is a presentation pass, not this seam's job.
 */
export function formatAuthoredValue(
  field: Pick<AuthoredField, 'type' | 'variant'>,
  raw: unknown,
): string | null {
  if (raw === null || raw === undefined) return null;
  // Actor refs are ids ("person:<uuid>"), not display text - a card must never
  // wear a raw ref, so actor highlights stay silent until a resolving read
  // exists (the board item "Field-type matrix deferrals").
  if (field.type === 'actor') return null;
  // Prose is a document, not a card datum: silence on cards by design.
  if (field.type === 'text' && field.variant === 'prose') return null;
  // A many-cardinality value (string[]) joins for display (selection labels).
  if (Array.isArray(raw)) {
    const parts = raw.filter((v): v is string => typeof v === 'string' && !!v.trim());
    return parts.length > 0 ? parts.join(', ') : null;
  }
  if (field.type === 'number') {
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    return null;
  }
  // text / date / selection: a non-empty string displays as itself (a date's
  // canonical grain string - 2026-Q3 - is already compact card text).
  if (typeof raw === 'string') return raw.trim() || null;
  return null;
}

// --- The module-kit bridge --------------------------------------------------

/**
 * A flow's field definitions AS a module definition (docs/FLOW.md "Flow-scoped
 * fields"): same facets, same validation, same write path as a context's
 * fields, but the module key is the flow's own `flow:<definitionId>` namespace
 * - values arrive on an item because it JOINED the flow (membership reach),
 * never ambiently. Flow fields are item truth (`public`); a private-visibility
 * flow field is the gatekeeper context's annotation and is rejected by the
 * write spine before this bridge is built.
 */
export function flowToModuleDefinition(
  flowId: string,
  label: string,
  fields: readonly AuthoredField[],
): ModuleDefinition {
  return {
    key: flowModuleKey(flowId),
    label,
    description: `Fields defined by the ${label} flow.`,
    fields: fields.map((f) => ({
      key: f.key,
      label: f.label,
      // The user-word -> stored-type boundary (docs/FIELDS_AND_TAGS.md §2):
      // selection -> enum, actor -> ref (person/group), date -> timevalue (an
      // exact day is the finest grain; stored day strings parse unchanged).
      type:
        f.type === 'selection'
          ? 'enum'
          : f.type === 'actor'
            ? 'ref'
            : f.type === 'date'
              ? 'timevalue'
              : f.type,
      source: 'authored',
      aggregate: 'none',
      visibility: 'public',
      help: f.description,
      ...(f.type === 'selection' ? { options: (f.values ?? []).map((v) => v.value) } : {}),
      ...(f.type === 'actor' ? { refKinds: ACTOR_FIELD_REF_KINDS } : {}),
      ...(f.cardinality === 'many' ? { cardinality: 'many' as const } : {}),
      ...(f.variant === 'prose' ? { variant: 'prose' } : {}),
    })),
  };
}

/** What an authored actor field may point at: people and groups, never items or
 *  automations. An actor field records who - it never seats, grants, or notifies
 *  by itself (docs/FIELDS_AND_TAGS.md §2). */
export const ACTOR_FIELD_REF_KINDS = ['person', 'group'] as const;

// --- Flow payloads (docs/FLOW.md) --------------------------------------------

/** The `payload` jsonb of a `flow` definition (docs/FLOW.md): the flow's own
 *  display **name** (free vocabulary, editable; the definition `key` is the
 *  stable id and never changes), the ordered stages (how items move), and the
 *  flow's own field definitions (the process's vocabulary - gate scorecard,
 *  business case - distinct from the owning group's general fields). */
export type FlowPayload = {
  /** The editable display name. May be '' on pre-name rows; readers go through
   *  `flowDisplayName`, which falls back to a humanized key. */
  name: string;
  stages: FlowPhaseRow[];
  fields: AuthoredField[];
  /** The flow's fact rules (docs/THRESHOLDS.md §2). Optional here because this
   *  is the legacy shape check; `parseFlowDefinition` (flow-definition.ts) is
   *  the authoritative parser the write path runs, and it always normalizes
   *  them to an array. */
  factRules?: FlowFactRule[];
};

/** Validate a flow definition's payload: `{ name?, stages, fields? }`. Stages
 *  are definition-only (a flow never carries per-member state - positions and
 *  overlays live on `flow_memberships`); the row parser drops any unknown
 *  properties, so a stray binding cannot land here. `fields` are optional
 *  (default `[]`), each validated like an authored field. `name` defaults to
 *  '' (old rows; display falls back via `flowDisplayName`). */
export function parseFlowPayload(value: unknown): Result<FlowPayload> {
  if (!isRecord(value)) return errOf('invalid_input', m('flowErrors.definition.payloadNotObject'));
  if (value.name !== undefined && typeof value.name !== 'string') {
    return errOf('invalid_input', m('flowErrors.definition.nameNotString'));
  }
  const name = typeof value.name === 'string' ? value.name.trim() : '';
  const parsed = parseFlowPhaseRows(value.stages);
  if (!parsed.ok) return parsed;
  let fields: AuthoredField[] = [];
  if (value.fields !== undefined) {
    const parsedFields = parseAuthoredFields(value.fields);
    if (!parsedFields.ok) return parsedFields;
    fields = parsedFields.value;
  }
  return ok({ name, stages: parsed.value, fields });
}

/** A flow's display name: the payload's own `name`, falling back to a
 *  humanized definition key for rows authored before names existed (the
 *  board-singleton era key `flow` reads "Flow"). The KEY stays the stable id;
 *  this is presentation only. */
export function flowDisplayName(key: string, payload?: { name?: string }): string {
  const name = payload?.name?.trim();
  if (name) return name;
  return key
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// --- MCP derivation (docs/FLOW.md: definitions are machine-readable) ---

/** One authored field as a JSON-Schema property. The description IS the text an
 *  agent reasons over - the ontology artifact doing double duty. The MCP tool
 *  surface (`contextToolSchemas`, one write tool per field) is slice 8; this
 *  per-field primitive is the reusable core, kept here on the definition anchor. */
export function fieldToJsonSchema(field: AuthoredField): Record<string, unknown> {
  const hint = FIELD_TYPE_SCHEMA_HINT[field.type];
  const base: Record<string, unknown> = {
    description: hint ? `${field.description} ${hint}` : field.description,
  };
  const scalar = scalarJsonSchema(field);
  if (field.cardinality === 'many') {
    return { ...base, type: 'array', uniqueItems: true, items: scalar };
  }
  return { ...base, ...scalar };
}

/** Machine-facing format hints, appended to the field's own description (the
 *  description stays the ontology; the hint says how to write the value). */
const FIELD_TYPE_SCHEMA_HINT: Partial<Record<AuthoredFieldType, string>> = {
  date: 'A time at any grain: day (2026-07-15), week (2026-W24), month (2026-07), quarter (2026-Q3), year (2026), or a "start/end" range.',
  actor: 'An actor reference: "person:<id>" or "group:<id>".',
};

function scalarJsonSchema(field: AuthoredField): Record<string, unknown> {
  switch (field.type) {
    case 'number':
      return { type: 'number' };
    case 'date':
      return { type: 'string' };
    case 'actor':
      return { type: 'string', pattern: '^(person|group):.+' };
    case 'selection':
      return {
        type: 'string',
        enum: (field.values ?? []).map((v) => v.value),
        ...((field.values ?? []).some((v) => v.description)
          ? {
              // Non-standard but widely understood: value meanings ride along.
              'x-value-descriptions': Object.fromEntries(
                (field.values ?? [])
                  .filter((v) => v.description)
                  .map((v) => [v.value, v.description]),
              ),
            }
          : {}),
      };
    case 'text':
      return field.variant === 'prose'
        ? { type: 'string', contentMediaType: 'text/markdown' }
        : { type: 'string' };
  }
}
